import { z } from 'zod';

/**
 * Organisation settings — today, the manager the roster report goes to.
 *
 * `GET` and `PUT /api/settings`. One row for the whole organisation, so the
 * manager is the same on every phone that signs in, and it is the SERVER's copy
 * that Send report reads: a manager kept only on the device is a report that
 * goes nowhere.
 *
 * Responses are snake_case (`phone_display`), request bodies camelCase — the
 * same split as staff.
 */
export const settingsSchema = z
  .object({
    manager: z.object({
      name: z.string(),
      /** E.164, or null when no manager is set. */
      phone: z.string().nullable(),
      phone_display: z.string().nullable(),
    }),
  })
  .transform((raw) => ({
    manager: {
      name: raw.manager.name,
      phone: raw.manager.phone,
      /** `+61 412 345 678`. Display only — never post it back. */
      phoneDisplay: raw.manager.phone_display,
    },
  }));

export type Settings = z.infer<typeof settingsSchema>;

/**
 * Australian and Nepali mobiles, as the form accepts them — deliberately the
 * same generous pattern as the staff and phone-auth schemas, and deliberately
 * DUPLICATED from the server's `normaliseMobile` (see the boundary note in
 * AGENTS.md). The server normalises; this only stops a typo at the form.
 */
const MOBILE =
  /^(?:(?:\+?61\s?|0)?4\d{2}\s?\d{3}\s?\d{3}|(?:\+?977[\s-]?)?9[678]\d[\s-]?\d{3}[\s-]?\d{4})$/;

export const updateSettingsInputSchema = z.object({
  manager: z.object({
    name: z.string().trim().max(120),
    /** Null clears the manager's number; a blank string is not a number. */
    phone: z
      .string()
      .trim()
      .regex(MOBILE, {
        message: 'Enter an Australian or Nepali mobile — +61 4XX XXX XXX or +977 98X XXX XXXX.',
      })
      .nullable(),
  }),
});

export type UpdateSettingsInput = z.input<typeof updateSettingsInputSchema>;
